/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/mouse-events-have-key-events */

// TODO: add back in if uncommenting stuff below
import { PropTypes } from 'prop-types';
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { withTheme } from 'styled-components';

import { Badge } from '@ge/components/badge';
import { Icon, Icons } from '@ge/components/icon';
import { Loader } from '@ge/components/loader';
import { SortDirection } from '@ge/components/table/models/sort-direction';
import { useColumnState } from '@ge/components/table/use-column-state';
import {
  Placeholders,
  MobileTab,
  AssetConnectionStatus,
  SortType,
  AssetState,
  EntityType
} from '@ge/models/constants';
import { useSiteRealTimeInfo } from '@ge/shared/data-hooks';
import { getConnectionStatusDuration } from '@ge/shared/util/time-date';
import { getTurbineStateColor, getTurbineStateType } from '@ge/shared/util/turbine-state';
import { globalColors } from '@ge/tokens/colors';
import { elevations } from '@ge/tokens/elevations';

export const SiteLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: 100vh;
  &.hidden {
    display: none;
  }
`;

const TurbineContent = styled.div`
  display: flex;
  gap: 4px 8px;
  padding: 20px 20px 11px 8px;
  border-bottom: 1px solid ${globalColors.grey11};
  box-shadow: 0 2px 3px -1px rgba(0, 0, 0, 0.1);
  &:hover,
  &:focus {
    background-color: ${globalColors.blue7};
  }
  &:hover + .arrow-right-icon {
    fill: ${globalColors.blue8};
  }
  p &.hidden {
    display: none;
  }
`;

const ArrowRight = styled.div`
  margin-left: auto !important;
  padding-right: 15px;
  padding-top: ${(props) => (props.isPower ? '10px' : '0')};
`;

const TurbineBadge = styled.div`
  display: grid;
  justify-items: start;
  margin-top: -10px;
`;

const TurbineIcon = styled(Icon).attrs((props) => ({
  size: props.size,
  icon: Icons.TURBINE,
  color: props.theme.entityDetails.siteHeaderIconColor,
}))`
  position: relative;
  left: 5px;
  top: ${(props) => (props.isDisconnected ? '9px' : '6px')};
`;

const TurbineName = styled.div`
  line-height: 17px;
  color: ${(props) => props.theme.entityDetails.headerIconColor};
  font-family: GE Inspira;
  font-weight: 400;
  font-size: 22px;
  .asset-name {
    display: inline-block;
    max-width: 110px;
    min-width: 10px;
    white-space: nowrap;
    overflow: hidden !important;
    text-overflow: ellipsis;
  }
`;
const TurbineDetailWrapper = styled.div`
  display: flex;
`;
const TurbineNameText = styled.div`
  max-width: 60%;
  font-size: 19px;
`;
const TurbineDetails = styled.div`
  color: ${globalColors.grey12};
  font-size: 13px;
  font-weight: 500;
  font-family: GE Inspira;
  font-style: italic;
  margin-left: 10px;
  .turbine-subdetails {
    color: ${globalColors.grey2};
  }
  .turbine-separator {
    color: #979797;
    margin: 5px;
  }
`;
const TrippedCode = styled.div`
  color: ${globalColors.white};
  font-weight: 500;
  white-space: nowrap;
  font-size: 10px;
  margin-top: -15px;
  margin-bottom: 5px;
  background-color: ${globalColors.red2};
  color: white;
  width: 270px;
  border-radius: 7px;
  font-style: normal;
  padding-left: 5px;
  padding-right: 5px;
  text-align: left;
  overflow: hidden !important;
  text-overflow: ellipsis;
`;
const TurbineText = styled.div`
  color: ${globalColors.grey12};
  font-weight: 500;
  font-family: GE Inspira;
  white-space: nowrap;
  font-size: 13px;
  margin-top: 10px;
  font-style: normal;
`;

const StatusBadge = styled(Badge)`
  z-index: ${elevations.P10};
  position: absolute;
  opacity: ${(props) => (props.isDisconnected ? '0.4' : '')};
`;
const AssetMetric = {
  NAME: 'name',
  STATE: 'state',
};

const ChevronIconRight = styled(Icon).attrs((props) => ({
  size: 12,
  icon: Icons.CHEVRON,
  color: props.theme.entityDetails.headerIconColor,
  rotate: -90,
}))``;

const capitalizeFirstLetter = (string) => {
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
};
const getStatusString = (state, prevState, connection) =>
  (connection && connection?.value === AssetConnectionStatus.NOCOMM) ||
    connection?.value === AssetConnectionStatus.NO_DATA
    ? `No Comm for ${getConnectionStatusDuration(connection) ?? Placeholders.DASH
    }; previously ${capitalizeFirstLetter(prevState?.value)}`
    : state
      ? `${capitalizeFirstLetter(state?.value)} for ${getConnectionStatusDuration(state) ?? Placeholders.DASH
      }; previously ${capitalizeFirstLetter(prevState?.value)}`
      : '';
const getFaultStateString = (faultState) =>
  faultState.value && faultState.value.trim() !== ''
    ? `Code ${faultState.value} : ${faultState.desc && faultState.desc !== '' ? faultState.desc : ''
    }`
    : '';

const turbinesList = (item, translateFn) => {
  const isDisconnected = item.connection?.value === AssetConnectionStatus.NOCOMM;

  return (
    <>
      <TurbineBadge>
        <StatusBadge
          small
          color={getTurbineStateColor(item.state?.value)}
          label={getTurbineStateType(item.state?.value)}
          isDisconnected={isDisconnected}
        />
        <TurbineIcon size="25" isDisconnected={isDisconnected} />
      </TurbineBadge>
      <TurbineName>
        {item.state &&
          item.state?.value === AssetState.TRIPPED &&
          item.faultState &&
          item.faultState?.value && (
            <TrippedCode isHidden={item.state?.value === AssetState.TRIPPED}>
              {getFaultStateString(item.faultState)}
            </TrippedCode>
          )}
        <TurbineDetailWrapper>
          <TurbineNameText>{item.name}</TurbineNameText>
          <TurbineDetails>
            {translateFn('mobile.power', 'Power')}:{' '}
            <span className="turbine-subdetails">{item.power ?? Placeholders.DASH}</span>
            <span className="turbine-separator">|</span>
            {translateFn('mobile.wind', 'Wind')}:{' '}
            <span className="turbine-subdetails">{item.wind ?? Placeholders.DASH}</span>
          </TurbineDetails>
        </TurbineDetailWrapper>
        <TurbineText>{getStatusString(item.state, item.prevState, item.connection)}</TurbineText>
      </TurbineName>
      <ArrowRight isPower={item.state}>
        <ChevronIconRight className="arrow-right-icon" />
      </ArrowRight>
    </>
  );
};

const AssetMobileList = ({ onRowSelect, siteId, active, filterText, getFilteredAssets }) => {
  const { t } = useTranslation(['general'], {
    useSuspense: false,
  });

  // Table state management
  const { data, isLoading } = useSiteRealTimeInfo({
    siteId: siteId,
    type: EntityType.ASSET
  });
  const { assets = [] } = data || {};


  const [pageLoad, setPageLoad] = useState(false);
  const [allAssets, setAllAssets] = useState([]);
  useEffect(() => {
    if (allAssets.length && !active) {
      setAllAssets([]);
    }
  }, [allAssets, active]);
  useEffect(() => {
    setTimeout(() => {
      setAllAssets(getFilteredAssets(assets));
    }, 100);
  }, [assets, filterText, getFilteredAssets]);



  const getRowDefs = useCallback(
    (value) => {
      return {
        children: turbinesList(value, t),
        key: `listing-${value.name}-${value.id}`,
        onClick: (e) => onRowSelect(e, value, MobileTab.TURBINE),
      };
    },
    [onRowSelect, t],
  );
  /**
   * Build a specific row for the provided value and visible columns.
   */
  const buildRow = useCallback(
    ({ asset }) => {
      // Handle both virtualized and non-virtualized scenarios. If the table is not
      // scrollable, we cannot virtualize the rows because they are all shown.

      if (!asset.id) {
        return null;
      }
      const { children, key, onClick } = getRowDefs(asset);
      // check if the provided list of selected row Id's matches the current row.
      return (
        <TurbineContent key={key} onClick={onClick} className={!active && 'hidden'}>
          {children}
        </TurbineContent>
      );
    },
    [getRowDefs, active],
  );
  if (!allAssets.length && isLoading)
    return (
      <SiteLoader className={active ? '' : 'hidden'}>
        <Loader />
      </SiteLoader>
    );
  if (!active)
    return null
  return allAssets.map((value, idx) => buildRow({ asset: value, idx }));
};

AssetMobileList.propTypes = {
  onRowSelect: PropTypes.func,
  theme: PropTypes.instanceOf(Object),
  assets: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  isLoading: PropTypes.bool,
  siteId: PropTypes.string,
  filterText: PropTypes.string,
  getFilteredAssets: PropTypes.func,
};
AssetMobileList.defaultProps = {
  siteId: '',
  isLoading: true,
  filterText: '',
};
export default withTheme(AssetMobileList);
